import { supabase } from '@/lib/supabase';
import { getOrCreatePwaDeviceId, sendSupabasePushNotification } from '@/lib/pwa';
import type { CreateOrderInput, CreateOrderResponse } from '../types';
import { normalizePhone, toMpesaFormat } from '@/lib/phone';
import {
  getOrCreateCheckoutAttempt,
  getResolvedCheckoutAttempt,
  resolveCheckoutAttempt,
} from './checkout-attempt';

interface CreateOrderRpcResult {
  order_id: string;
  order_number?: string | number;
  order_reference?: string;
  created: boolean;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizeUuid(value?: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  return UUID_PATTERN.test(trimmed) ? trimmed : null;
}

export async function createOrder(input: CreateOrderInput): Promise<CreateOrderResponse> {
  try {
    if (!supabase) {
      return {
        success: false,
        message: 'Supabase is not configured for order creation.',
      };
    }

    const phoneInput = (input.phone || '').trim();
    if (!phoneInput) {
      return {
        success: false,
        message: 'Phone number is required before creating an order.',
      };
    }

    const normalizedPhone = normalizePhone(phoneInput);
    if (!normalizedPhone.success) {
      return {
        success: false,
        message: normalizedPhone.error.message,
      };
    }

    if (!input.branchId) {
      return {
        success: false,
        message: 'Select a branch before creating an order.',
      };
    }

    if (!input.deliveryAddress?.street?.trim()) {
      return {
        success: false,
        message: 'Delivery address is required before creating an order.',
      };
    }

    if (input.items.length === 0) {
      return {
        success: false,
        message: 'Your cart is empty.',
      };
    }

    const existingAttempt = getResolvedCheckoutAttempt(input);
    if (existingAttempt?.orderId) {
      return {
        success: true,
        orderId: existingAttempt.orderId,
        orderNumber: existingAttempt.orderNumber,
        orderReference: existingAttempt.orderReference,
      };
    }

    const checkoutAttempt = getOrCreateCheckoutAttempt(input);
    const { data, error } = await supabase.rpc('create_order_with_items', {
      p_customer_phone: normalizedPhone.data,
      p_customer_name: input.customerName?.trim() || null,
      p_delivery_address: input.deliveryAddress.street.trim(),
      p_delivery_notes: input.notes?.trim() || null,
      p_branch_id: input.branchId,
      p_delivery_fee: input.deliveryFee || 0,
      p_vat_amount: input.tax || 0,
      p_delivery_zone_id: normalizeUuid(input.deliveryZoneId),
      p_distance_km: input.distanceKm || null,
      p_idempotency_key: checkoutAttempt.idempotencyKey,
      p_trigger_source: 'cart_checkout',
      p_session_id: checkoutAttempt.sessionId,
      p_cart_fingerprint: checkoutAttempt.cartFingerprint,
      p_post_order_action: input.postOrderAction || 'payment',
      p_metadata: {
        customer_profile_id: input.customerProfileId || null,
        delivery_method: input.deliveryMethod,
        payment_method: input.paymentMethod,
        requested_total: input.total,
      },
      p_items: input.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName || '',
        quantity: item.quantity,
        unit_price: item.unitPrice,
        variant: item.variantId || null,
      })),
    });

    if (error) {
      throw new Error(error.message || 'Failed to create order');
    }

    const orderResult = Array.isArray(data)
      ? (data[0] as CreateOrderRpcResult | undefined)
      : (data as CreateOrderRpcResult | null);

    if (!orderResult?.order_id) {
      throw new Error('Failed to create order');
    }

    if (orderResult.order_id) {
      resolveCheckoutAttempt(input, {
        orderId: orderResult.order_id,
        orderNumber: orderResult.order_number,
        orderReference: orderResult.order_reference,
      });

      try {
        await sendSupabasePushNotification({
          action: 'send_order_notification',
          deviceId: getOrCreatePwaDeviceId(),
          title: 'Order received',
          body: `Your order ${orderResult.order_reference || orderResult.order_number || ''} has been received by Karebe.`,
          url: `/order-confirmation?orderId=${orderResult.order_id}`,
          tag: `order-${orderResult.order_id}`,
        });
      } catch (notificationError) {
        console.warn('[Checkout] Unable to send order notification:', notificationError);
      }

      try {
        await sendSupabasePushNotification({
          action: 'send_admin_order_notification',
          audience: 'admins',
          title: 'New order placed',
          body: `${input.customerName || 'A customer'} placed order ${orderResult.order_reference || orderResult.order_number || ''}.`,
          url: '/admin/orders',
          tag: `admin-order-${orderResult.order_id}`,
        });
      } catch (notificationError) {
        console.warn('[Checkout] Unable to send admin order notification:', notificationError);
      }
    }

    return {
      success: true,
      orderId: orderResult.order_id,
      orderNumber: orderResult.order_number,
      orderReference: orderResult.order_reference,
      requiresPayment: input.postOrderAction === 'payment',
    };
  } catch (error) {
    console.error('Create order error:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to create order',
    };
  }
}

export async function initiateMpesaPayment(
  _orderId: string,
  phoneNumber: string,
  _amount: number
): Promise<{ success: boolean; checkoutRequestId?: string; message?: string }> {
  try {
    const mpesaPhoneResult = toMpesaFormat(phoneNumber);
    if (!mpesaPhoneResult.success) {
      return {
        success: false,
        message: 'Invalid phone number for M-Pesa payment.',
      };
    }

    return {
      success: false,
      message: 'Automated STK Push is not configured in the Supabase checkout flow. Use the order payment screen instructions instead.',
    };
  } catch (error) {
    console.error('M-Pesa payment error:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Payment failed',
    };
  }
}

export async function checkPaymentStatus(orderId: string): Promise<{
  status: 'pending' | 'completed' | 'failed';
  message?: string;
}> {
  try {
    if (!supabase) {
      return { status: 'pending' };
    }

    const { data, error } = await supabase
      .from('orders')
      .select('payment_status')
      .eq('id', orderId)
      .single();

    if (error) throw error;

    return {
      status: data.payment_status === 'completed' ? 'completed' : 
              data.payment_status === 'failed' ? 'failed' : 'pending',
    };
  } catch (error) {
    console.error('Check payment status error:', error);
    return { status: 'pending' };
  }
}
